import { useEffect, useState, type FormEvent } from "react";
import { GUVENILIR_CIHAZ_GUN, TOTP_PENCERE_SANIYE } from "@koclukpanel/contracts";
import { useQuery } from "@tanstack/react-query";
import { Alan, Buton, GirisKabugu, Ikon, KodAlani, NotKutusu, OnayKutusu } from "@koclukpanel/ui";
import { ApiHatasi, girisYap, ikinciAsama, recaptchaDurumu } from "./api";
import { girisJetonu } from "./recaptcha";
import { BakimBandi } from "./BakimBandi";

interface GirisProps {
  /**
   * Giriş tamamlandı.
   *
   * `kalanYedekKod` YALNIZCA yedek kodla girildiğinde dolu (G-AUT-02a);
   * doğrulama uygulamasıyla girildiğinde `null` — harcanan bir şey yok,
   * söylenecek bir sayı da yok.
   */
  onGirdi: (kalanYedekKod: number | null) => void;
  /**
   * "Şifremi unuttum" bağlantısı.
   *
   * İSTEĞE BAĞLI: bağlantı yalnızca verildiğinde çiziliyor. Çağıran
   * taraf akışı yönetiyor; bu ekran kendi içinde bir sayfa daha
   * açmıyor.
   */
  onSifremiUnuttum?: () => void;
  /**
   * Hesap kilitlendi — `kod: "KILITLI"`.
   *
   * Kilit bir FORM HATASI değil, ayrı bir durum: kullanıcının bu
   * ekranda yapabileceği hiçbir şey kalmıyor. Küçük kırmızı bir
   * satır olarak göstermek, aynı şifreyi bir kez daha denemeye
   * davet ederdi.
   */
  onKilitlendi?: (kalanDakika: number | null) => void;
}

/** Doğrulama kodu altı haneli. */
const HANE = 6;

function kalanSaniye(): number {
  return TOTP_PENCERE_SANIYE - (Math.floor(Date.now() / 1000) % TOTP_PENCERE_SANIYE);
}

/**
 * GİRİŞ EKRANI
 *
 * İki aşamalı: şifre doğrulanınca 2FA kurulu kullanıcılarda oturum
 * AÇILMIYOR, ikinci aşama bileti dönüyor (G-AUT-01).
 *
 * ================================================================
 * ÜÇ EKRAN, TEK BİLEŞEN
 * ================================================================
 * Çizim üç ayrı tuval veriyor — `Giris` · `GirisDogrulama` ·
 * `GirisKurtarmaKodu` — ama üçü de AYNI akışın adımları ve ikisi
 * aynı uca gidiyor. Ayrı bileşenlere bölseydik aşama jetonunu
 * aralarında taşımak gerekirdi; jeton tek kullanımlık ve beş hatalı
 * denemede iptal oluyor, yani dolaştığı her yer bir kayıp riski.
 *
 * ================================================================
 * HATA MESAJI AYRIM YAPMIYOR
 * ================================================================
 * Sunucu hatalı kimlik ile hatalı şifre için AYNI yanıtı veriyor
 * (G-AUT-06 — kullanıcı sayımı koruması). Arayüz de o mesajı AYNEN
 * gösteriyor; "bu e-posta kayıtlı değil" gibi bir metin üretmek,
 * sunucunun özenle sakladığı bilgiyi arayüzden sızdırmak olurdu.
 *
 * ================================================================
 * TEK ALAN, ÜÇ KİMLİK — VE ALAN `type="text"` (G-AUT-00)
 * ================================================================
 * Alan e-posta, telefon ya da T.C. kimlik numarası kabul ediyor ve
 * kullanıcı hangisini yazdığını SEÇMİYOR; sunucu çözüyor.
 *
 * `type="email"` BIRAKILAMAZDI: tarayıcı o türde kendi doğrulamasını
 * yapar ve `05551112233` yazan kullanıcıyı formu göndermeden
 * reddeder. Yani sunucu üç yolu da kabul ederken ekran ikisini
 * sessizce keserdi — hata hiçbir yerde görünmez, yalnızca "giriş
 * çalışmıyor" olurdu.
 *
 * İstemci tarafında BİÇİM DOĞRULAMASI DA YOK, aynı sebeple: hangi
 * türü yazdığını ekranın bilmesi gerekmiyor ve "geçersiz numara"
 * demek, kimliğin ne olduğunu ele veren bir sinyal olurdu.
 *
 * ================================================================
 * "BU CİHAZI 30 GÜN HATIRLA" — ÇİZİMDEN BİR EKRAN SONRADA
 * ================================================================
 * Tuvalde onay kutusu ŞİFRE ekranında duruyor; burada İKİNCİ AŞAMADA
 * (kod ve kurtarma kodu ekranlarında). Sebep işlevsel: şifre
 * ekranında kullanıcının 2FA'sı olup olmadığı henüz bilinmiyor ve
 * 2FA'sı olmayan biri için kutunun hiçbir karşılığı yok — hatırlanacak
 * bir şey yok, çünkü zaten kod sorulmuyor.
 *
 * Özelliğin kendisi 04.09.2026'da Emre'nin kararıyla yazıldı ve
 * şartnamede G-AUT-01c olarak dokuz kurallık bir tabloyla duruyor.
 */
export function Giris({ onGirdi, onSifremiUnuttum, onKilitlendi }: GirisProps) {
  const [asama, setAsama] = useState<"sifre" | "kod" | "kurtarma">("sifre");
  const [kimlik, setKimlik] = useState("");
  const [sifre, setSifre] = useState("");
  const [kod, setKod] = useState("");
  const [jeton, setJeton] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const [calisiyor, setCalisiyor] = useState(false);

  /*
    ================================================================
    "BU CİHAZI 30 GÜN HATIRLA" — G-AUT-01c
    ================================================================
    ÇİZİMDEN BİR EKRAN SONRAYA ALINDI ve sebebi işlevsel: tuvalde kutu
    ŞİFRE ekranında duruyor, ama orada henüz kullanıcının 2FA'sı olup
    olmadığı BİLİNMİYOR. 2FA'sı olmayan biri kutuyu işaretlediğinde
    hiçbir şey olmazdı — hatırlanacak bir şey yok, çünkü zaten kod
    sorulmuyor. Hiçbir şey yapmayan bir onay kutusu, çizimin en kötü
    okunuşu olurdu.

    Kutu artık ikinci aşamada: göründüğü her yerde gerçekten
    çalışıyor.

    VARSAYILAN İŞARETSİZ (şartname G-AUT-01c kural 9): güvenlik
    gevşetmesi kullanıcının açık tercihi olmalı.
  */
  const [cihaziHatirla, setCihaziHatirla] = useState(false);

  // Site anahtari yoksa Google'a HICBIR istek gitmiyor (bkz. recaptcha.ts).
  const { data: rc } = useQuery({
    queryKey: ["recaptcha-durum"],
    queryFn: recaptchaDurumu,
    // Bir kez alinmasi yeterli; giris ekraninda dururken degismez.
    staleTime: Infinity,
    retry: false,
  });

  /*
    Kodun yenilenmesine kaç saniye kaldığı. Sunucuya SORULMUYOR:
    TOTP penceresi sabit 30 saniye ve sınır Unix zamanının katlarında
    — yani tarayıcı da aynı sayıyı hesaplayabiliyor. Sormak, saniyede
    bir istek atmak olurdu.

    Sayaç YALNIZCA kod aşamasında işliyor; şifre ekranında dururken
    saniyede bir yeniden çizim yapmanın anlamı yok.
  */
  const [saniye, setSaniye] = useState(kalanSaniye);
  useEffect(() => {
    if (asama !== "kod") return;
    setSaniye(kalanSaniye());
    const s = setInterval(() => setSaniye(kalanSaniye()), 1000);
    return () => clearInterval(s);
  }, [asama]);

  async function sifreGonder(e: FormEvent): Promise<void> {
    e.preventDefault();
    setHata(null);
    setCalisiyor(true);
    try {
      const rcJeton = rc?.aktif ? await girisJetonu(rc.siteAnahtari) : null;
      const sonuc = await girisYap(kimlik.trim(), sifre, rcJeton);
      if (sonuc.ikinciAsama) {
        setJeton(sonuc.asamaJetonu);
        setAsama("kod");
        // Sifre bellekte TUTULMUYOR: ikinci asamada gerekmiyor ve
        // sekmede acik kalan bir formda durmasinin sebebi yok.
        setSifre("");
      } else {
        onGirdi(null);
      }
    } catch (err) {
      /*
        Kilit ayrı bir EKRAN, form hatası değil. Kullanıcının burada
        yapabileceği bir şey kalmadı; kırmızı bir satır göstermek onu
        aynı şifreyi bir kez daha denemeye davet ederdi.
      */
      if (err instanceof ApiHatasi && err.kod === "KILITLI" && onKilitlendi) {
        // Sunucu sayı gönderiyor; `ayrinti` sözlüğü metin tipli
        // olduğu için tipe değil DEĞERE bakıyoruz.
        const ham: unknown = err.ayrinti?.kalanDakika;
        onKilitlendi(typeof ham === "number" ? ham : null);
        return;
      }
      setHata(err instanceof ApiHatasi ? err.message : "Giriş yapılamadı.");
    } finally {
      setCalisiyor(false);
    }
  }

  async function kodGonder(e: FormEvent): Promise<void> {
    e.preventDefault();
    setHata(null);
    setCalisiyor(true);
    try {
      const sonuc = await ikinciAsama(jeton, kod.trim(), cihaziHatirla);
      onGirdi(sonuc.kalanYedekKod);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Kod doğrulanamadı.");
      setKod("");
      // Bilet iptal edildiyse bastan baslamak gerekiyor; kullaniciyi kod
      // ekraninda birakmak onu sonsuza kadar denemeye iterdi.
      if (err instanceof ApiHatasi && err.kod === "BILET_GECERSIZ") {
        setAsama("sifre");
        setJeton("");
      }
    } finally {
      setCalisiyor(false);
    }
  }

  /** Aşama değiştirirken yarım kalan kod ve hata taşınmıyor. */
  function asamayaGec(yeni: "sifre" | "kod" | "kurtarma"): void {
    setAsama(yeni);
    setKod("");
    setHata(null);
  }

  const hataSatiri = hata && (
    <div
      role="alert"
      className="rounded-alan bg-hata-500/10 text-hata-700 flex items-start gap-2 px-3 py-2 text-sm"
    >
      <Ikon ad="hata" boyut="kucuk" className="mt-0.5 shrink-0" />
      <span>{hata}</span>
    </div>
  );

  /*
    reCAPTCHA marka bildirimi. Google'in kullanim sartlari, rozet
    gizlendiginde bu metnin gosterilmesini ZORUNLU kiliyor. Koruma
    kapaliyken hic gosterilmiyor — olmayan bir seyi bildirmek
    kullaniciyi yaniltir.
  */
  const recaptchaNotu = rc?.aktif ? (
    <>
      {" "}
      Bu site reCAPTCHA ile korunuyor; Google{" "}
      <a
        href="https://policies.google.com/privacy"
        target="_blank"
        rel="noreferrer"
        className="hover:text-notr-700 underline"
      >
        Gizlilik Politikası
      </a>{" "}
      ve{" "}
      <a
        href="https://policies.google.com/terms"
        target="_blank"
        rel="noreferrer"
        className="hover:text-notr-700 underline"
      >
        Şartları
      </a>{" "}
      geçerlidir.
    </>
  ) : null;

  if (asama === "sifre") {
    return (
      <GirisKabugu
        baslik="Giriş yapın"
        aciklama="E-posta, telefon ya da T.C. kimlik numaranızla devam edin."
        altNot={
          <>
            Hata mesajı <strong className="text-notr-800">bilginin kayıtlı olup olmadığını</strong>{" "}
            söylemez — söyleseydi saldırgana hangi kayıtların var olduğunu bildirirdik.
            {recaptchaNotu}
          </>
        }
      >
        {/*
          BAKIM BANDI GİRİŞ EKRANINDA DA — F-SEC-06.

          `/bakim/durum` kimlik istemiyor, yani bakım bilgisi giriş
          ÖNCESİNDE gösterilebiliyor. Yalnızca içeride gösterseydik
          kullanıcı şifresini yazıp girdikten SONRA öğrenirdi — oysa
          bakımda yapabileceği bir şey yok ve bunu en başta bilmeli.
        */}
        <BakimBandi />

        {hataSatiri}

        <form onSubmit={(e) => void sifreGonder(e)} className="flex flex-col gap-5">
          <Alan
            etiket="E-posta, telefon veya T.C. kimlik no"
            type="text"
            autoComplete="username"
            required
            value={kimlik}
            onChange={(e) => setKimlik(e.target.value)}
          />

          <div className="flex flex-col gap-1.5">
            <Alan
              etiket="Şifre"
              type="password"
              autoComplete="current-password"
              required
              value={sifre}
              onChange={(e) => setSifre(e.target.value)}
            />
            {/*
              Bağlantı ŞİFRE ALANININ ALTINDA, düğmenin değil: çizimin
              yeri burası ve mantığı da öyle — şifreyi yazamayan kişi
              hatayı orada fark ediyor, düğmeye kadar inmeden.
            */}
            {onSifremiUnuttum && (
              <button
                type="button"
                onClick={onSifremiUnuttum}
                className="text-marka-700 hover:text-marka-600 self-end text-[13px] hover:underline"
              >
                Şifremi unuttum
              </button>
            )}
          </div>

          <Buton type="submit" boyut="genis" yukleniyor={calisiyor}>
            Devam
            <Ikon ad="sagOk" boyut="kucuk" />
          </Buton>
        </form>
      </GirisKabugu>
    );
  }

  if (asama === "kod") {
    return (
      <GirisKabugu
        baslik="Doğrulama kodu"
        aciklama={
          <>
            Doğrulama uygulamanızdaki <strong className="text-notr-800">6 haneli</strong> kodu
            girin.
          </>
        }
        altNot={
          <>
            Kod <strong className="text-notr-800">SMS ile gönderilmez</strong>. SMS, SIM değiştirme
            saldırısına açık; doğrulama uygulaması cihazdan çıkmaz.
          </>
        }
      >
        {hataSatiri}

        <form onSubmit={(e) => void kodGonder(e)} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2.5">
            <KodAlani deger={kod} onDegisti={setKod} hane={HANE} devreDisi={calisiyor} odakla />
            {/*
              Sayaç "acele et" demiyor, "yanlış kod girdiysen beklemen
              gereken süre bu kadar" diyor. Süresi geçmiş bir kodu
              tekrar tekrar denemek, ikinci aşamanın beş denemelik
              sınırını boşa harcatıyordu.
            */}
            <p className="text-notr-600 text-[13px]" aria-live="off">
              Kod <strong className="text-notr-800">{saniye} saniye</strong> sonra yenilenir
            </p>
          </div>

          <OnayKutusu
            checked={cihaziHatirla}
            onChange={(e) => setCihaziHatirla(e.target.checked)}
            etiket={`Bu cihazı ${GUVENILIR_CIHAZ_GUN} gün hatırla`}
            yardim="Bu tarayıcıda doğrulama kodu bir daha sorulmaz. Ortak bir bilgisayardaysanız işaretlemeyin."
          />

          <Buton type="submit" boyut="genis" yukleniyor={calisiyor} disabled={kod.length < HANE}>
            Doğrula
          </Buton>

          <button
            type="button"
            onClick={() => asamayaGec("kurtarma")}
            className="text-marka-700 hover:text-marka-600 self-center text-[13.5px] hover:underline"
          >
            Kurtarma kodu ile giriş yap
          </button>
        </form>
      </GirisKabugu>
    );
  }

  return (
    <GirisKabugu
      baslik="Kurtarma kodu"
      aciklama={
        <>
          Kurulumda aldığınız <strong className="text-notr-800">tek kullanımlık</strong> kodlardan
          birini girin.
        </>
      }
      altNot="On kodun tamamı biterse hesabı yalnızca yönetici sıfırlayabilir — ve bu işlem denetim kaydına düşer."
    >
      {hataSatiri}

      <form onSubmit={(e) => void kodGonder(e)} className="flex flex-col gap-5">
        <Alan
          etiket="Kurtarma kodu"
          autoComplete="one-time-code"
          autoFocus
          required
          value={kod}
          onChange={(e) => setKod(e.target.value)}
        />

        <NotKutusu ton="uyari">
          Bu kod kullanıldıktan sonra <strong>geçersiz olur</strong>. Kalan kod sayınız girişten
          sonra ekranda gösterilecek.
        </NotKutusu>

        <OnayKutusu
          checked={cihaziHatirla}
          onChange={(e) => setCihaziHatirla(e.target.checked)}
          etiket={`Bu cihazı ${GUVENILIR_CIHAZ_GUN} gün hatırla`}
          yardim="Bu tarayıcıda doğrulama kodu bir daha sorulmaz. Ortak bir bilgisayardaysanız işaretlemeyin."
        />

        <Buton type="submit" boyut="genis" yukleniyor={calisiyor}>
          Giriş yap
        </Buton>

        <button
          type="button"
          onClick={() => asamayaGec("kod")}
          className="text-marka-700 hover:text-marka-600 self-center text-[13.5px] hover:underline"
        >
          Doğrulama uygulamasına geri dön
        </button>
      </form>
    </GirisKabugu>
  );
}
